/**
 * World position → position on a map image.
 *
 * Every SW:TOR map is a rectangle of world space drawn to an image, so placing a
 * point is one linear transform per axis (platform INTEGRATING.md §6). Bounds are
 * in combat-log units, so combat-log `x, y` go in exactly as the log reports
 * them — no scaling, no axis swap, no flip.
 *
 * Results are PERCENTAGES of the image, so the server, the Electron client and
 * admin-web all draw at whatever size they like from the same numbers. This is
 * the one implementation of the transform; nothing else should repeat it.
 */
function spans(bounds) {
    const spanX = bounds.maxX - bounds.minX;
    const spanY = bounds.maxY - bounds.minY;
    return spanX > 0 && spanY > 0 ? { spanX, spanY } : null;
}
/**
 * Where world `(x, y)` falls on a map, as a percentage of the image. Values
 * outside 0-100 mean the point is off this map. Null for degenerate bounds.
 */
export function worldToMapPercent(bounds, x, y) {
    const s = spans(bounds);
    if (!s)
        return null;
    return {
        x: ((x - bounds.minX) / s.spanX) * 100,
        y: ((y - bounds.minY) / s.spanY) * 100,
    };
}
/**
 * A world-unit radius as percentages of the image. `rx` and `ry` differ when the
 * map's scale differs per axis, so the circle is drawn as an ellipse.
 */
export function radiusToMapPercent(bounds, radius) {
    const s = spans(bounds);
    if (!s)
        return null;
    return { rx: (radius / s.spanX) * 100, ry: (radius / s.spanY) * 100 };
}
/**
 * Where a rectangle of world space (another map's bounds, e.g. a zone inside its
 * planet) falls on a map, as a percentage rectangle of the image. Null for
 * degenerate bounds.
 */
export function boundsToMapPercentRect(bounds, inner) {
    const from = worldToMapPercent(bounds, inner.minX, inner.minY);
    const to = worldToMapPercent(bounds, inner.maxX, inner.maxY);
    if (!from || !to)
        return null;
    return { x: from.x, y: from.y, w: to.x - from.x, h: to.y - from.y };
}
/** Whether world `(x, y)` lies inside the map's rectangle (edges included). */
export function isWithinMap(bounds, x, y) {
    return x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY;
}
